import {
  ref,
  computed,
  onMounted,
  inject,
} from '@vue/composition-api';
import { captureException } from '@sentry/browser';
import { mainStore, eventTracker } from 'partners-widget-core';
import useRoute from '@/compositions/useRoute';
import { BASKET_TAB_VALUE, TITLE_PAGE } from '@/enums';
import { checkMobile } from '@/utils/deviceCheck';
import { getFormatDateTime } from '@/utils/dateTime';
import { updateArr } from '@/utils/updateProductsArr';

import useHttpClient from '@/compositions/useHttpClient';
import { useBasketContext } from '@/compositions/basket/basketStorage';
import { useLocationContext } from '@/compositions/location/locationStorage';
import useCommonModal from '@/compositions/useCommonModal';
import useAuthorizations from '@/compositions/user/useAuthorizations';
import useCashbackBlock from '@/compositions/useCashbackBlock';
import useWishListBasket from '@/compositions/useWishListBasket';
import useLoading from '@/compositions/useLoading';
import useProductsInShelf from '@/compositions/useProductsInShelf';
import useDigitalDataManager from '@/compositions/ddm/useDigitalDataManager';
import { getBasketItemWithLink } from '@/utils/formatters/formattedProduct';
import { useMeta } from '@/compositions/useMeta';

const MAX_ITEMS_SHELF = 6;

const DEFAULT_OPTIMAL_DELIVERY_DATA = {
  isPvzBook24: null,
  leftToGetFreeDelivery: null,
  price: null,
};

const useBasketPage = () => {
  const route = useRoute();
  const { checkout } = useHttpClient();
  const basketContext = useBasketContext();
  const { modal, getModalComponent } = useCommonModal();
  const { isAuthorized } = useAuthorizations();
  const cashbackBlock = useCashbackBlock();
  const { cityLocation, isUaOrGb } = useLocationContext();
  const { setLoadingFlags, loadingFlags } = useLoading();
  const wishListBasket = useWishListBasket();
  const productsInShelfComposition = useProductsInShelf();
  const { viewPage } = useDigitalDataManager();
  const singleSpa = inject('singleSpa');

  const isMobile = checkMobile();

  // FIXME: костыль чтоб отрабатывали старые события корзины в ddl
  basketContext.firstRender.value = false;

  const currentTab = ref(BASKET_TAB_VALUE);

  const isEmptyBasket = ref(false);

  const basketItems = ref([]);

  const cheapestDeliveries = ref([]);

  const shipmentDate = ref(null);

  const isSyncGift = ref(true);

  const optimalDeliveryData = ref({ ...DEFAULT_OPTIMAL_DELIVERY_DATA });

  const productsInShelf = ref([]);

  const availableBonuses = ref(null);

  const giftList = ref([]);

  const isBasket = computed(() => currentTab.value === BASKET_TAB_VALUE);

  const showSidebar = computed(() => isBasket.value && !isEmptyBasket.value);

  const isFirstRender = ref(true);

  const showEmptyComponent = computed(() => (isBasket.value && isEmptyBasket.value)
        || (!isBasket.value && wishListBasket.isEmptyWishList.value));

  const isDeletedAllTab = computed(() => {
    const deletedList = basketItems.value.filter(item => item.isDeleted);
    const itemsWithoutGift = basketItems.value.filter(item => !item.isGift);
    return deletedList.length === itemsWithoutGift.length;
  });

  const checkIsFreeDelivery = type => cheapestDeliveries.value.find(item => item.type === type)?.price === '0.0000';

  const isCourierFree = computed(() => checkIsFreeDelivery('courier'));

  const isPostFree = computed(() => checkIsFreeDelivery('post'));

  const isPvzFree = computed(() => checkIsFreeDelivery('pvz'));

  const isHasFreeDelivery = computed(() => isCourierFree.value || isPostFree.value || isPvzFree.value);

  const isDeliveryError = computed(() => {
    if (optimalDeliveryData.value.isPvzBook24 || isHasFreeDelivery.value) return false;
    return !optimalDeliveryData.value.price || !optimalDeliveryData.value.leftToGetFreeDelivery;
  });

  const showDeliveryInfo = computed(() => !isDeliveryError.value
        && !isDeletedAllTab.value
        && typeof optimalDeliveryData.value.isPvzBook24 === 'boolean');

  const hasGiftInBasket = computed(() => basketItems.value.find(item => item.isGift));

  const showChangeGift = computed(() => !!giftList.value.length);

  const isLoadingFetchBasket = computed(() => !!(loadingFlags.value?.fetchBasket ?? []).length);

  const isLoadingChangeAllItems = computed(() => !!(loadingFlags.value?.showGlobalLoader ?? []).length);

  const isLoadingFetchOnMounted = computed(() => !!(loadingFlags.value?.fetchOnMounted ?? []).length);

  const countInTab = computed(() => (isBasket.value ? basketItems.value.length : wishListBasket.wishListTotal.value));

  const unavailableItemsLength = computed(() => basketItems.value.filter(item => !item.product.quantity)?.length);

  const fetchLinksByNomcodes = async (nomcodes) => {
    try {
      return mainStore.store.env.productLinkMethod(nomcodes);
    } catch (err) {
      captureException(err);
      return null;
    }
  };

  const syncBasket = async () => basketContext.syncBasket({
    isAddFirstGift: isSyncGift.value,
  })
    .then(async (dataRes) => {
      const nomcodes = dataRes.basketItem.map(item => item.product.nomcode);
      const links = (mainStore.store.env.productLinkMethod && nomcodes.length) ? await fetchLinksByNomcodes(nomcodes) : null;

      return { basket: dataRes, links };
    })
    .then(async ({ basket, links }) => {
      const newItems = links ? basket.basketItem.map(item => getBasketItemWithLink(item, links)) : basket.basketItem;
      basketItems.value = updateArr(basketItems.value, (newItems ?? []));
      basketItems.value = basketItems.value.sort((a, b) => a.product.quantity - b.product.quantity);
      isEmptyBasket.value = !basketItems.value.length;
      return basket;
    })
    .catch((error) => {
      captureException(error);
    });

  const fetchDeliverySummary = async () => {
    await checkout.fetchDeliverySummary({
      strategy: 'basket',
      basketId: basketContext.basketId.value,
      locationCode: cityLocation.value,
    })
      .then(({ cheapestDeliveries: deliveries, optimalDelivery, shipment }) => {
        if (deliveries && Array.isArray(deliveries)) {
          cheapestDeliveries.value = deliveries.filter(item => item.price);
        }

        if (shipment && shipment.date) {
          shipmentDate.value = getFormatDateTime(shipment.date, 'd MMMM');
        }

        if (optimalDelivery) {
          optimalDeliveryData.value = optimalDelivery;
        }
      })
      .catch((error) => {
        optimalDeliveryData.value = { ...DEFAULT_OPTIMAL_DELIVERY_DATA };
        captureException(error);
      });
  };

  const fetchSummary = async () => {
    await checkout.fetchSummary({
      strategy: 'basket',
      basketId: basketContext.basketId.value,
      locationCode: cityLocation.value,
      recommendation: cashbackBlock.recommendationProduct.value,
    })
      .then((data) => {
        if (data?.loyaltyForCheckout?.availableBonuses) {
          availableBonuses.value = data.loyaltyForCheckout.availableBonuses;
          cashbackBlock.plBonusesForOrder.value = Number(data.amount.earnedBonus);
        }
      })
      .catch((error) => {
        captureException(error);
      });
  };

  const fetchPotentialGifts = async () => {
    await checkout.fetchPotentialGifts({
      strategy: 'basket',
      basketId: basketContext.basketId.value,
      locationCode: cityLocation.value,
    })
      .then((data) => {
        if (data && Array.isArray(data)) {
          giftList.value = data;
        }
      })
      .catch((error) => {
        giftList.value = [];
        captureException(error);
      });
  };

  const fetchProductsPopular = async () => {
    await productsInShelfComposition.getProducts();
    productsInShelf.value = productsInShelfComposition.products.value.slice(0, MAX_ITEMS_SHELF);
  };

  const initialBasket = async () => {
    setLoadingFlags({ flagName: 'fetchBasket', flag: true });

    await syncBasket()
      .then(async (result) => {
        if (isFirstRender.value) {
          eventTracker.dispatchEvent('view-basket', result);
        }

        if (!isEmptyBasket.value) {
          const promiseList = [];

          if (hasGiftInBasket.value) {
            promiseList.push(fetchPotentialGifts());
          }

          if (isAuthorized.value) {
            const productIdList = basketContext.productIdList.value.filter(item => !item.isGift);
            promiseList.push(cashbackBlock.getRecommendationProducts(productIdList));
          }

          await Promise.allSettled([
            ...promiseList,
            fetchSummary(),
            fetchDeliverySummary(),
          ]);
        }
      })
      .finally(() => {
        setLoadingFlags({ flagName: 'fetchBasket', flag: false });
        if (isFirstRender.value) {
          isFirstRender.value = false;
        }
      });
  };

  const handlerOpenAuthModal = () => {};

  const handlerClickButtonGoCheckout = () => {
    if (!isDeletedAllTab.value && !isUaOrGb.value) {
      eventTracker.dispatchEvent('checkout-redirect', {
        basket: basketItems.value,
        amount: basketContext.amount.value,
        discountAmount: basketContext.discountAmount.value,
        quantity: basketContext.quantity.value,
        weight: basketContext.weight.value,
      });

      singleSpa.navigateToUrl(`/${mainStore.store.env.prefixAppUrl}#/personal/order/make`);
    }
  };

  const handlerMoveToWishList = async ({ productId }) => {
    const index = basketItems.value.findIndex(item => item.product.id === productId);
    const hasGift = !!basketItems.value.filter(item => item.isGift).length;
    const lengthItemsBeforeDeleteItem = basketItems.value.length;

    if (index !== -1) {
      const isGift = basketItems.value[index].isGift;

      basketItems.value[index].isLoading = true;
      basketItems.value[index].isMoved = true;

      await basketContext.deleteBasketItem({ itemId: basketItems.value[index].id, isFetchBasket: false });
      await initialBasket();

      if (!isGift) {
        let indexItem = index;
        if (lengthItemsBeforeDeleteItem === 2 && hasGift) {
          indexItem -= 1;
        }
        basketItems.value[indexItem].isLoading = false;
      }
    }
  };

  const handlerRemoveItem = async ({ productId }) => {
    const index = basketItems.value.findIndex(item => item.product.id === productId);
    const hasGift = !!basketItems.value.filter(item => item.isGift).length;
    const lengthItemsBeforeDeleteItem = basketItems.value.length;

    if (index !== -1) {
      const isGift = basketItems.value[index].isGift;
      if (isGift) {
        isSyncGift.value = false;
      }

      basketItems.value[index].isLoading = true;

      await basketContext.deleteBasketItem({ itemId: basketItems.value[index].id, isFetchBasket: false });

      await initialBasket();
      eventTracker.dispatchEvent('remove-product', {
        basketItem: basketItems.value,
        amount: basketContext.amount.value,
        discountAmount: basketContext.discountAmount.value,
        quantity: basketContext.quantity.value,
        weight: basketContext.weight.value,
      });

      if (!isGift) {
        let indexItem = index;
        if (lengthItemsBeforeDeleteItem === 2 && hasGift) {
          indexItem -= 1;
        }
        basketItems.value[indexItem].isLoading = false;
      }
    }
  };

  const handlerSpliceItem = ({ productId }) => {
    const index = basketItems.value.findIndex(item => item.product.id === productId);
    if (index !== -1 && basketItems.value[index].isDeleted) {
      basketItems.value.splice(index, 1);
      isEmptyBasket.value = !basketItems.value.length;
    }
  };

  const handlerRestoreItem = async ({ quantity, productId }) => {
    const index = basketItems.value.findIndex(item => item.product.id === productId);
    if (index !== -1) {
      basketItems.value[index].isLoading = true;

      await basketContext.addToBasket({ productId, quantity, isFetchBasket: false });
      eventTracker.dispatchEvent('restore-product', {
        basketItem: basketItems.value,
        amount: basketContext.amount.value,
        discountAmount: basketContext.discountAmount.value,
        quantity: basketContext.quantity.value,
        weight: basketContext.weight.value,
      });
      await initialBasket();

      basketItems.value[index].isLoading = false;
    }
  };

  const handlerUpdateQuantityItem = async ({ quantity, productId }) => {
    const index = basketItems.value.findIndex(item => item.product.id === productId);
    if (index !== -1) {
      basketItems.value[index].isLoading = true;

      const result = await basketContext.updateBasketItem({
        itemId: basketItems.value[index].id,
        quantity,
        isFetchBasket: true,
      });
      eventTracker.dispatchEvent('change-quantity', result);

      await initialBasket();

      basketItems.value[index].isLoading = false;
    }
  };

  const handlerAddToBasket = async ({ productId }) => {
    const index = wishListBasket.wishList.value.findIndex(item => item.id === productId);
    if (index !== -1) {
      wishListBasket.wishList.value[index].isLoadingAddToBasket = true;

      await basketContext.addToBasket({ productId, quantity: 1, isFetchBasket: false });
      await initialBasket();

      wishListBasket.wishList.value[index].isLoadingAddToBasket = false;
    }
  };

  const handlerChangeAllItems = async () => {
    setLoadingFlags({ flagName: 'showGlobalLoader', flag: true });

    if (isDeletedAllTab.value) {
      const productItems = basketItems.value.map(item => ({
        productId: item.product.id,
        quantity: item.quantity,
      }));
      await basketContext.addToBasketItems({ productItems, isFetchBasket: false });

      eventTracker.dispatchEvent('restore-products', {
        basketItem: basketItems.value,
        amount: basketContext.amount.value,
        discountAmount: basketContext.discountAmount.value,
        quantity: basketContext.quantity.value,
        weight: basketContext.weight.value,
      });
    } else {
      const basketIdItems = basketItems.value.map(item => item.id);
      await basketContext.deleteBasketItems({ basketIdItems, isFetchBasket: false });

      eventTracker.dispatchEvent('remove-all-products', {
        basketItem: basketItems.value,
        amount: basketContext.amount.value,
        discountAmount: basketContext.discountAmount.value,
        quantity: basketContext.quantity.value,
        weight: basketContext.weight.value,
      });
    }
    await initialBasket();

    setLoadingFlags({ flagName: 'showGlobalLoader', flag: false });
  };

  const handlerRemoveUnavailable = async () => {
    setLoadingFlags({ flagName: 'showGlobalLoader', flag: true });
    const basketIdItems = basketItems.value
      .filter(item => !item.product.quantity)
      .map(item => item.id);
    await basketContext.deleteBasketItems({ basketIdItems, isFetchBasket: false });
    await initialBasket();
    setLoadingFlags({ flagName: 'showGlobalLoader', flag: false });
  };

  const handlerAddToBasketGift = async ({ productId }) => {
    setLoadingFlags({ flagName: 'showGlobalLoader', flag: true });

    await basketContext.addToBasket({ productId, quantity: 1, isFetchBasket: false, asGift: true });
    await initialBasket();

    setLoadingFlags({ flagName: 'showGlobalLoader', flag: false });
  };

  const handlerClickNotAvailable = ({ productId }) => {
    modal.show({
      component: getModalComponent(),
      slots: {
        default: {
          component: () => import('@/components/FormSubscribeOnAvailableProduct/index.vue'),
          bind: {
            productId,
            isContainer: false,
          },
        },
      },
    });
  };

  const handlerClickChangeGift = () => {
    const nameModal = 'gift-list-basket';
    const giftProduct = basketItems.value.find(item => item.isGift);
    const componentGiftList = isMobile
      ? () => import('@/components/_mobile/GiftList/index.vue')
      : () => import('@/components/_desktop/GiftList/index.vue');

    modal.show({
      component: getModalComponent(),
      bind: {
        nameModal,
      },
      slots: {
        default: {
          component: componentGiftList,
          bind: {
            giftList: giftList.value,
            currentGiftId: giftProduct.product.id,
          },
          on: {
            addToBasketGift: async (payload) => {
              modal.hide(nameModal);
              await handlerAddToBasketGift(payload);
            },
          },
        },
      },
    });
  };

  const handlerUpdateBasket = async () => {
    setLoadingFlags({ flagName: 'showGlobalLoader', flag: true });
    await initialBasket();
    setLoadingFlags({ flagName: 'showGlobalLoader', flag: false });
  };

  const handleChangeTab = async (type) => {
    currentTab.value = type;

    if (!isBasket.value) {
      wishListBasket.showSkeleton.value = true;
      wishListBasket.wishList.value = [];
      await wishListBasket.fetchProductsWishList(1, true);
    }
  };

  const handlerLoadMoreWishList = async () => {
    await wishListBasket.loadMoreWishList();
  };

  onMounted(async () => {
    setLoadingFlags({ flagName: 'fetchOnMounted', flag: true });

    await Promise.allSettled([
      initialBasket(),
    ])
      .then(async () => {
        viewPage({
          ...route.value.meta.ddm,
          title: TITLE_PAGE,
        });

        await fetchProductsPopular();
      })
      .finally(() => {
        setLoadingFlags({ flagName: 'fetchOnMounted', flag: false });
      });
  });

  useMeta(() => ({
    title: TITLE_PAGE,
  }));

  return {
    crumbs: [
      { title: 'Главная', link: '/' },
      { title: 'Корзина', link: '/' },
    ],
    isFirstRender,
    productsInShelf,
    basketItems,
    cheapestDeliveries,
    shipmentDate,
    optimalDeliveryData,
    availableBonuses,
    amount: basketContext.amount,
    discountAmount: basketContext.discountAmount,
    quantity: basketContext.quantity,
    weight: basketContext.weight,
    isAuthorized,
    currentTab,
    isBasket,
    isEmptyBasket,
    showSidebar,
    showEmptyComponent,
    showDeliveryInfo,
    isLoadingFetchBasket,
    isLoadingChangeAllItems,
    isLoadingFetchOnMounted,
    isDeletedAllTab,
    isHasFreeDelivery,
    isUaOrGb,
    giftList,
    showChangeGift,
    countInTab,
    unavailableItemsLength,
    cashback: cashbackBlock.plBonusesForOrder,
    isLoadingCashback: cashbackBlock.isLoading,
    initialBasket,
    handleChangeTab,
    handlerOpenAuthModal,
    handlerClickButtonGoCheckout,
    handlerMoveToWishList,
    handlerRemoveItem,
    handlerSpliceItem,
    handlerRestoreItem,
    handlerUpdateQuantityItem,
    handlerChangeAllItems,
    handlerClickNotAvailable,
    handlerUpdateBasket,
    handlerLoadMoreWishList,
    handlerClickChangeGift,
    isEmptyWishList: wishListBasket.isEmptyWishList,
    wishList: wishListBasket.wishList,
    isLoadingFetchProductsWishList: wishListBasket.isLoadingFetchProductsWishList,
    isMayLoadMoreWishList: wishListBasket.isMayLoadMore,
    showSkeletonWishList: wishListBasket.showSkeleton,
    handlerSpliceWishItem: data => wishListBasket.handlerSpliceWishItem(data),
    handlerRestoreWishItem: data => wishListBasket.handlerRestoreWishItem(data),
    handlerRemoveWishItem: data => wishListBasket.handlerRemoveWishItem(data),
    handlerAddToBasket,
    handlerRemoveUnavailable,
  };
};

export default useBasketPage;
